import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import React, { useRef, useState, useLayoutEffect } from 'react';
import { V2VerifiedIcon, V2QuestionIcon, VerifiedAltIcon, QuestionAltIcon } from '@paypalcorp/pp-react-icons';
import { useTheme } from '@paypalcorp/pp-react-theme';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import '../__generated__/styles/tooltip.css';

var styles = {"ppvx_tooltip":"ppvx_tooltip___2-9-4","ppvx_tooltip__icon":"ppvx_tooltip__icon___2-9-4","ppvx_tooltip__icon--verified":"ppvx_tooltip__icon--verified___2-9-4","ppvx_tooltip--position_bottom":"ppvx_tooltip--position_bottom___2-9-4","ppvx_tooltip--position_left":"ppvx_tooltip--position_left___2-9-4","ppvx_tooltip--position_right":"ppvx_tooltip--position_right___2-9-4","ppvx_tooltip--tip_left":"ppvx_tooltip--tip_left___2-9-4","ppvx_tooltip--tip_right":"ppvx_tooltip--tip_right___2-9-4","ppvx_tooltip__icon--screenreader":"ppvx_tooltip__icon--screenreader___2-9-4","ppvx_tooltip__image":"ppvx_tooltip__image___2-9-4","ppvx_tooltip__image--top-left":"ppvx_tooltip__image--top-left___2-9-4","ppvx_tooltip__image--top-center":"ppvx_tooltip__image--top-center___2-9-4","ppvx_tooltip__image--bottom-left":"ppvx_tooltip__image--bottom-left___2-9-4","ppvx_tooltip__image--bottom-center":"ppvx_tooltip__image--bottom-center___2-9-4","ppvx_tooltip__icon--svg":"ppvx_tooltip__icon--svg___2-9-4","ppvx--v2":"ppvx--v2___2-9-4","ppvx--v1":"ppvx--v1___2-9-4","ppvx_tooltip__svg":"ppvx_tooltip__svg___2-9-4"};

var tooltip;
var tooltipOrigin;
/* eslint-disable complexity */

var Tooltip = function (_ref) {
  var _clsx;

  var _ref$name = _ref.name,
      name = _ref$name === void 0 ? 'Tooltip' : _ref$name,
      description = _ref.description,
      _ref$screenReaderText = _ref.screenReaderText,
      screenReaderText = _ref$screenReaderText === void 0 ? name : _ref$screenReaderText,
      position = _ref.position,
      verifiedIcon = _ref.verifiedIcon,
      windowId = _ref.windowId,
      className = _ref.className,
      triggerClassName = _ref.triggerClassName,
      tooltipClassName = _ref.tooltipClassName,
      containerRef = _ref.containerRef,
      onOpen = _ref.onOpen,
      onClose = _ref.onClose,
      imageElement = _ref.imageElement,
      _ref$imagePosition = _ref.imagePosition,
      imagePosition = _ref$imagePosition === void 0 ? 'top-left' : _ref$imagePosition,
      theme = _ref.theme;
  var wrapperRef = useRef();
  var tooltipOriginRef = useRef();
  var tooltipRef = useRef();

  var _theme = useTheme('Tooltip', theme);

  var isThemeV2 = _theme && _theme === 'v2';

  if (_theme !== 'v2') {
    console.warn('Tooltips 1.0 styles are deprecated. Use Tooltips 2.0 styles and refer to our Migration Guide on how to use 2.0 styles/components.');
  }

  var isTouchDevice = typeof window !== 'undefined' && 'ontouchstart' in window || typeof navigator !== 'undefined' && (navigator.maxTouchPoints || navigator.msMaxTouchPoints);

  var noop = function () {};

  var tooltipWindow;
  var containerPos;
  var scrollX;
  var scrollY;
  var width;
  var height;
  var originWidth;
  var originHeight;
  var originPos;
  var maxWidth;
  var iconSpacing;
  var caretWidth;
  var caretHeight;
  var caretSpaceHorizontal;
  var spacingOffset;
  var tooltipIconSize;

  var _useState = useState({
    isOpen: false,
    currentEvent: null
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var isOpen = state.isOpen,
      currentEvent = state.currentEvent;

  var getWrapperCurrent = function (w) {
    return (
      /* istanbul ignore next */
      w && w.current ? w.current : null
    );
  };

  var setTooltipPosition = function (top, left) {
    var _containerPos, _containerPos2;

    var containerElm = getWrapperCurrent(containerRef);

    var _ref2 = containerElm ? [top - ((_containerPos = containerPos) === null || _containerPos === void 0 ? void 0 : _containerPos.top) - scrollY + containerElm.scrollTop, left - ((_containerPos2 = containerPos) === null || _containerPos2 === void 0 ? void 0 : _containerPos2.left) - scrollX + containerElm.scrollLeft] : [top, left],
        _ref3 = _slicedToArray(_ref2, 2),
        offsetTop = _ref3[0],
        offsetLeft = _ref3[1];

    tooltip.style.top = "".concat(offsetTop, "px");
    tooltip.style.left = "".concat(offsetLeft, "px");
    tooltip.style.maxWidth = "".concat(maxWidth, "px");
  };
  /* istanbul ignore next */


  var getTooltipBoundary = function () {
    var providedWindow = windowId ? document.getElementById(windowId) : '';

    if (providedWindow) {
      var pos = providedWindow.getBoundingClientRect();
      return {
        windowEl: providedWindow,
        top: pos.top,
        right: pos.right,
        bottom: pos.bottom,
        left: pos.left
      };
    }

    return {
      windowEl: window,
      top: 0,
      right: window.innerWidth || document.documentElement.clientWidth,
      bottom: window.innerHeight || document.documentElement.clientHeight,
      left: 0
    };
  };

  var positionTopCenter = function () {
    // tooltip bubble starts at bottom left edge of its origin (icon/button)
    // and calculates moves from there:
    var topCenterX = originPos.left - width / 2 + originWidth / 2 + scrollX;
    var topCenterY = originPos.top - height - spacingOffset + scrollY + iconSpacing;
    setTooltipPosition(topCenterY, topCenterX);
  };
  /* istanbul ignore next */


  var positionBottom = function (top, left) {
    var offsetY = top + scrollY + height + originHeight + spacingOffset * 2 - iconSpacing * 2;
    setTooltipPosition(offsetY, left);
  };

  var positionLeft = function () {
    var buttonWidth = originWidth / 2 - tooltipIconSize / 2; // tooltip bubble starts at bottom left edge of its origin (icon/button)
    // and calculates X and Y moves from there:

    var leftPosX = originPos.left - width + buttonWidth - spacingOffset + scrollX;
    var leftPosY = originPos.top - height / 2 + originHeight / 2 + scrollY;
    tooltip.classList.add(styles['ppvx_tooltip--position_left']);
    setTooltipPosition(leftPosY, leftPosX);
  };

  var positionRight = function () {
    var buttonWidth = originWidth / 2 + tooltipIconSize / 2; // tooltip bubble starts at bottom left edge of its origin (icon/button)
    // and calculates X and Y moves from there:

    var rightPosX = originPos.left + buttonWidth + spacingOffset + scrollX;
    var rightPosY = originPos.top - height / 2 + originHeight / 2 + scrollY;
    tooltip.classList.add(styles['ppvx_tooltip--position_right']);
    setTooltipPosition(rightPosY, rightPosX);
  };

  var selfPosition =
  /* istanbul ignore next */
  function () {
    positionTopCenter();
    var boundary = tooltip.getBoundingClientRect();

    if (boundary.top < tooltipWindow.top || boundary.top < 0) {
      // position: bottom
      positionBottom(boundary.top, boundary.left);
      tooltip.classList.add(styles['ppvx_tooltip--position_bottom']); // store new boundary

      boundary = tooltip.getBoundingClientRect();
    } // note: if both left and right are out of bounds keep it centered


    if (!(boundary.left < tooltipWindow.left && boundary.left + maxWidth > tooltipWindow.right)) {
      var sideShift = caretSpaceHorizontal + caretWidth / 2;
      var offsetY = boundary.top + scrollY;
      var offsetX = boundary.left + scrollX;

      if (boundary.left < tooltipWindow.left) {
        // position: top-right, bottom-right (with left tooltip)
        offsetX = offsetX + width / 2 - sideShift;
        tooltip.classList.add(styles['ppvx_tooltip--tip_left']);
      }

      if (boundary.left + maxWidth > tooltipWindow.right) {
        // position: top-left, bottom-left (with right tooltip)
        offsetX = offsetX - width / 2 + sideShift;
        tooltip.classList.add(styles['ppvx_tooltip--tip_right']);
      }

      setTooltipPosition(offsetY, offsetX);
    }
  }; // for keyboard focus and mouseover


  var setOpen = function (evt) {
    evt.stopPropagation();
    setState({
      isOpen: true,
      currentEvent: evt.type
    });
    // eslint-disable-next-line
    onOpen && onOpen(evt, name);
  };

  var clearOpen = function (evt) {
    var activeElement = document.activeElement; // istanbul ignore next

    if (evt.type === 'resize' && isTouchDevice && activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
      return;
    }

    setState({
      isOpen: false,
      currentEvent: null
    }); // eslint-disable-next-line

    onClose && onClose(name);
    /* istanbul ignore else */

    if (tooltipWindow && tooltipWindow.windowEl) {
      var _tooltipWindow = tooltipWindow,
          windowEl = _tooltipWindow.windowEl;
      windowEl.removeEventListener('scroll', clearOpen);
      windowEl.removeEventListener('mousewheel', clearOpen);
    }
  };

  var setWidth = function () {
    var tooltipMaxWidthPx = getComputedStyle(tooltip, 'style').maxWidth;
    var tooltipMaxWidth = parseInt(tooltipMaxWidthPx.split('px')[0], 10);
    var winLeft = tooltipWindow.left;
    var winRight = tooltipWindow.right;
    var iconLeft = tooltipOrigin.getBoundingClientRect().left;
    var availableLeftSpace = iconLeft - winLeft + iconSpacing;
    var availableRightSpace = winRight - iconLeft - iconSpacing - tooltipIconSize;
    var halfMaxWidth = tooltipMaxWidth / 2; // if left and right of tooltip are each > than half the maxWidth, use default max width

    /* istanbul ignore next */

    if (availableLeftSpace > halfMaxWidth && availableRightSpace > halfMaxWidth) {
      maxWidth = tooltipMaxWidth;
    } else if ( // If both sides are less than half the maxwidth,
    // then tooltip bubble will be position center,
    // so use smaller side x 2 as max width since it's centered
    availableLeftSpace < halfMaxWidth && availableRightSpace < halfMaxWidth) {
      var smallerSide = availableLeftSpace < availableRightSpace ? availableLeftSpace : availableRightSpace;
      maxWidth = smallerSide * 2;
    } else {
      // take the bigger available space between the two
      var availableSpace = availableLeftSpace > availableRightSpace ? availableLeftSpace : availableRightSpace; // if it explicitly placed left/right adjust accordingly.

      if (position) {
        // Add the caret + 4px spacing
        availableSpace = availableSpace - spacingOffset - tooltipIconSize;
      } else {
        // Add the side shift dimension (since tooltip is position top/bottom with side shift)
        availableSpace += caretSpaceHorizontal + caretWidth / 2 - tooltipIconSize;
      }

      maxWidth = availableSpace > tooltipMaxWidth ? tooltipMaxWidth : availableSpace;
    }

    tooltip.style.maxWidth = "".concat(maxWidth, "px");
    var currentWidth = tooltip.getBoundingClientRect().width;
    /* istanbul ignore next */

    width = maxWidth < currentWidth ? maxWidth : currentWidth;
    height = tooltip.offsetHeight;
  };

  var positionTooltip = function () {
    tooltip = getWrapperCurrent(tooltipRef);
    tooltipOrigin = getWrapperCurrent(tooltipOriginRef);
    originPos = tooltipOrigin.getBoundingClientRect();
    var containerElm = getWrapperCurrent(containerRef);
    containerPos = containerElm && containerElm.getBoundingClientRect();
    tooltipWindow = getTooltipBoundary();
    width = tooltip.offsetWidth;
    height = tooltip.offsetHeight;
    originWidth = tooltipOrigin.offsetWidth;
    originHeight = tooltipOrigin.offsetHeight;
    scrollX = window.scrollX || window.pageXOffset;
    scrollY = window.scrollY || window.pageYOffset; // get tooltip's pointer dimensions:

    var rootFontSizePx = window && window.getComputedStyle(document.body).getPropertyValue('font-size') || '16px';
    var rootFontSize = parseInt(rootFontSizePx.split('px')[0], 10);
    caretWidth = isThemeV2 ? 1.125 * rootFontSize : 0.9375 * rootFontSize; // v2: 18px, v1: 15px

    caretHeight = isThemeV2 ? 0.75 * rootFontSize : 0.5625 * rootFontSize; // v2: 12px, v1: 9px

    caretSpaceHorizontal = 1 * rootFontSize; //16px spacing between caret and tooltip corner

    // 4px separation between caret and tooltip icon
    spacingOffset = caretHeight + 0.25 * rootFontSize;
    var targetSize = originWidth;
    tooltipIconSize = isThemeV2 ? 1.5 * rootFontSize : 1.125 * rootFontSize; // v2: 24px, v1: 18px
    // icon and target have different dimensions

    iconSpacing = targetSize / 2 - tooltipIconSize / 2; // set the initial width for tooltip.

    setWidth();
    var _tooltipWindow2 = tooltipWindow,
        windowEl = _tooltipWindow2.windowEl;
    windowEl.addEventListener('scroll', clearOpen);
    windowEl.addEventListener('mousewheel', clearOpen);

    switch (position) {
      case 'left':
        positionLeft();
        break;

      case 'right':
        positionRight();
        break;

      default:
        selfPosition();
    }
  };

  var handleClickOutside = function (evt) {
    var current = getWrapperCurrent(wrapperRef);
    /* istanbul ignore next */

    if (isOpen && current && !current.contains(evt.target)) {
      evt.stopPropagation();
      clearOpen(evt);
    }
  };

  var handleKey = function (evt) {
    /* istanbul ignore next */
    var keyCode = evt.key || evt.keyCode;

    switch (keyCode) {
      case 'Tab':
      case 27:
      case 'Escape':
      case 'Esc':
      case 9:
        clearOpen(evt);
        break;
    }
  };

  useLayoutEffect(function () {
    if (isOpen) {
      positionTooltip();
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKey);
      window.addEventListener('resize', clearOpen);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('resize', clearOpen);
    }

    return function () {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('resize', clearOpen);
    };
  });
  var tooltipOriginClasses = clsx(styles.ppvx_tooltip__icon, (_clsx = {}, _defineProperty(_clsx, styles['ppvx_tooltip__icon--verified'], verifiedIcon), _defineProperty(_clsx, styles['ppvx--v2'], isThemeV2), _clsx), styles['ppvx_tooltip__icon--svg'], triggerClassName);
  var tooltipClasses = clsx(styles.ppvx_tooltip, _defineProperty({}, styles['ppvx--v2'], isThemeV2), tooltipClassName);
  /* istanbul ignore next */

  var handleMouseLeave = function (evt) {
    if (tooltip && evt.relatedTarget !== tooltip) {
      evt.stopPropagation();
      clearOpen(evt);
    }
  };

  var imagePositionClasses = clsx([styles.ppvx_tooltip__image], [styles["ppvx_tooltip__image--".concat(imagePosition)]]);
  var displayImage = /*#__PURE__*/React.createElement("span", {
    className: imagePositionClasses
  }, imageElement);
  //@ts-ignore

  /* istanbul ignore next */
  var tooltipNode = isOpen && ReactDOM.createPortal( /*#__PURE__*/React.createElement("div", {
    ref: tooltipRef,
    className: tooltipClasses,
    id: name,
    role: "tooltip",
    "aria-hidden": !isOpen,
    onMouseLeave: currentEvent === 'focus' ? noop : clearOpen
  }, imageElement && (imagePosition === 'top-center' || imagePosition === 'top-left') && displayImage, description, imageElement && (imagePosition === 'bottom-center' || imagePosition === 'bottom-left') && displayImage), getWrapperCurrent(containerRef) || document.body);
  var TooltipIcon;
  /* istanbul ignore next */

  if (isThemeV2) {
    TooltipIcon = verifiedIcon ? V2VerifiedIcon : V2QuestionIcon;
  } else {
    TooltipIcon = verifiedIcon ? VerifiedAltIcon : QuestionAltIcon;
  }

  var tooltipSize = isThemeV2 ? 'sm' : 'xs';
  /* istanbul ignore next */

  var handleTouchStart = function (evt) {
    var current = getWrapperCurrent(wrapperRef);
    if (!current.contains(evt.target)) clearOpen(evt);
  };

  return /*#__PURE__*/React.createElement("span", {
    ref: wrapperRef,
    className: className
  }, /*#__PURE__*/React.createElement("button", _extends({
    type: "button",
    ref: tooltipOriginRef,
    className: tooltipOriginClasses
  }, isOpen ? {
    'aria-describedby': name
  } : {}, {
    onMouseEnter: currentEvent === 'focus' ? noop : setOpen,
    onMouseLeave: currentEvent === 'focus' ? noop : handleMouseLeave,
    onTouchEnd: function handleTouchEnd(evt) {
      evt.preventDefault(); // Toggle the state

      if (isOpen) {
        clearOpen(evt);
        document.removeEventListener('touchstart', handleTouchStart);
      } else {
        setOpen(evt);
        document.addEventListener('touchstart', handleTouchStart);
      }
    },
    onMouseDown: function handleMouseDown(evt) {
      return evt.preventDefault();
    },
    onFocus: isTouchDevice ? noop : setOpen
  }), /*#__PURE__*/React.createElement(TooltipIcon, {
    size: tooltipSize,
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    className: styles['ppvx_tooltip__icon--screenreader']
  }, screenReaderText)), tooltipNode);
};

export { Tooltip };
